import { Radio as UikitRadio } from "@circle-react-shared/uikit/Radio";
import { usePlans } from "@circle-react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { Radio } from "@headlessui/react-v2";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { planTiers } from "@/react/helpers/planHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { PlanFeatures } from "./PlanFeatures";

interface PlusCardProps {
  onClick: () => void;
  selected: boolean;
  showPrice: boolean;
}

const INTERVAL_TRANSLATIONS: Record<string, () => string> = {
  month: () =>
    t(
      "pricing_plans.plan_settings.plan_selection.plan_selector.monthly_interval",
    ),
  year: () =>
    t(
      "pricing_plans.plan_settings.plan_selection.plan_selector.yearly_interval",
    ),
  biannual: () =>
    t(
      "pricing_plans.plan_settings.plan_selection.plan_selector.biannual_interval",
    ),
  quarter: () =>
    t(
      "pricing_plans.plan_settings.plan_selection.plan_selector.quarterly_interval",
    ),
};

export const PlusCard = ({ selected, onClick, showPrice }: PlusCardProps) => {
  const { customPlanOfferPlan, customPlanOffer, tierDescriptions } = usePlans();
  const hasCustomOffer = !!customPlanOfferPlan;

  const displayPrice = hasCustomOffer
    ? customPlanOfferPlan.display_total
    : null;

  const intervalTranslation =
    hasCustomOffer && customPlanOffer
      ? (INTERVAL_TRANSLATIONS[customPlanOffer.custom_plus_interval] ??
        INTERVAL_TRANSLATIONS.month)
      : INTERVAL_TRANSLATIONS.month;

  return (
    <Radio
      key="plus"
      value="plus"
      as="div"
      className={classNames(
        "border-primary cursor-pointer border-b p-4 sm:p-6 flex items-start gap-3",
        selected ? "bg-gray-50 dark:bg-gray-900 border-t" : "bg-primary",
      )}
      onClick={onClick}
    >
      <div className="flex items-start">
        <UikitRadio
          value="plus"
          label=""
          checked={selected}
          onChange={() => {}}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-1">
            <Typography.LabelLg weight="semibold">
              {t(
                "pricing_plans.plan_settings.plan_selection.plan_selector.tiers.plus.title",
              )}
            </Typography.LabelLg>
            <div className="hidden sm:flex items-center">
              <Badge
                variant="indigo"
                label={t(
                  "pricing_plans.plan_settings.plan_selection.plan_selector.tiers.plus.badge",
                )}
                square
              />
            </div>
          </div>
          {showPrice && (
            <div className="flex flex-row items-center gap-1 align-middle">
              {!hasCustomOffer && (
                <Typography.BodyMd
                  weight="font-normal"
                  color="text-slate-700 dark:text-slate-300"
                  size="text-sm"
                >
                  {t(
                    "pricing_plans.plan_settings.plan_selection.plan_selector.tiers.plus.from",
                  )}
                </Typography.BodyMd>
              )}
              <Typography.TitleMd weight="font-bold">
                {displayPrice ??
                  t(
                    "pricing_plans.plan_settings.plan_selection.plan_selector.tiers.plus.starting_price",
                  )}
              </Typography.TitleMd>
              <Typography.BodyMd
                weight="font-normal"
                color="text-slate-700 dark:text-slate-300"
                size="text-sm"
              >
                {intervalTranslation()}
              </Typography.BodyMd>
            </div>
          )}
        </div>
        <div className="hidden sm:block">
          <Typography.BodySm weight="font-normal">
            {tierDescriptions?.plus ??
              t(
                "pricing_plans.plan_settings.plan_selection.plan_selector.tiers.plus.description",
              )}
          </Typography.BodySm>
        </div>
        <div
          className={classNames(
            "grid w-full overflow-hidden transition-all duration-300 ease-in-out",
            selected ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 min-w-0">
            <PlanFeatures
              planTier={planTiers.plus}
              isOnOffer={false}
              shouldHighlightFeatures
              containerClassName="mt-6"
            />
          </div>
        </div>
      </div>
    </Radio>
  );
};
